import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CWidgetStatsA,
} from "@coreui/react";
import { CChartLine } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import {
  cilGroup,
  cilNotes,
  cilPeople,
  cilUser,
} from "@coreui/icons";
import { useTranslationContext } from "@/context/TranslationContext";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import dashboardTranslations from "@/constants/translation/dashboard.translation";
import { useGetDashboardCounts, useGetUserChart } from "@/hooks/useRQDashboard";
import routes from "@/libs/routes";

const dashboard = dashboardTranslations;
const periodOptions = ["day", "week", "month", "year"];

const formatCount = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return "0";
  }
  return num.toLocaleString();
};

const Dashboard = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");

  const {
    data: countsResponse,
    isLoading: countsLoading,
    isError: countsError,
  } = useGetDashboardCounts();
  const {
    data: chartResponse,
    isLoading: chartLoading,
    isError: chartError,
  } = useGetUserChart({ period });

  useEffect(() => {
    if (countsError) {
      toast.error(t(dashboard.messages.fetch_counts_failed));
    }
  }, [countsError, t]);

  useEffect(() => {
    if (chartError) {
      toast.error(t(dashboard.messages.fetch_chart_failed));
    }
  }, [chartError, t]);

  const counts = countsResponse?.data || {};
  const graph = chartResponse?.data?.graph || [];

  const statCards = useMemo(
    () => [
      {
        key: "total_users",
        title: t(dashboard.total_users),
        value: counts.total_users,
        icon: cilUser,
        color: "primary",
        path: routes.userMaster.list,
      },
      {
        key: "total_communities",
        title: t(dashboard.total_communities),
        value: counts.total_communities,
        icon: cilPeople,
        color: "info",
      },
      {
        key: "total_community_posts",
        title: t(dashboard.total_community_posts),
        value: counts.total_community_posts,
        icon: cilNotes,
        color: "warning",
      },
      {
        key: "total_groups",
        title: t(dashboard.total_groups),
        value: counts.total_groups,
        icon: cilGroup,
        color: "success",
      },
    ],
    [counts, t]
  );

  const chartData = useMemo(() => {
    const labels = graph.map((item) => item.label);

    return {
      labels,
      datasets: [
        {
          label: t(dashboard.chart.users),
          data: graph.map((item) => Number(item.users) || 0),
          borderColor: "#7C3AED",
          backgroundColor: "rgba(124, 58, 237, 0.15)",
          tension: 0.35,
          fill: false,
        },
        {
          label: t(dashboard.chart.communities),
          data: graph.map((item) => Number(item.communities) || 0),
          borderColor: "#3399ff",
          backgroundColor: "rgba(51, 153, 255, 0.15)",
          tension: 0.35,
          fill: false,
        },
        {
          label: t(dashboard.chart.community_posts),
          data: graph.map((item) => Number(item.community_posts) || 0),
          borderColor: "#f9b115",
          backgroundColor: "rgba(249, 177, 21, 0.15)",
          tension: 0.35,
          fill: false,
        },
        {
          label: t(dashboard.chart.groups),
          data: graph.map((item) => Number(item.groups) || 0),
          borderColor: "#1b9e3e",
          backgroundColor: "rgba(27, 158, 62, 0.15)",
          tension: 0.35,
          fill: false,
        },
      ],
    };
  }, [graph, t]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: "#ffffff",
            usePointStyle: true,
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      scales: {
        x: {
          ticks: { color: "#9ca3af", maxRotation: 45, minRotation: 0 },
          grid: { color: "rgba(255, 255, 255, 0.08)" },
        },
        y: {
          beginAtZero: true,
          ticks: { color: "#9ca3af", precision: 0 },
          grid: { color: "rgba(255, 255, 255, 0.08)" },
        },
      },
    }),
    []
  );

  return (
    <div className="dashboard-page">
      <CRow className="g-3 mb-4">
        {statCards.map((card) => (
          <CCol key={card.key} xs={12} sm={6} xl={3}>
            <CWidgetStatsA
              className={`dashboard-stat-card mb-0${card.path ? " dashboard-stat-card--clickable" : ""}`}
              color={card.color}
              role={card.path ? "button" : undefined}
              tabIndex={card.path ? 0 : undefined}
              onClick={card.path ? () => navigate(card.path) : undefined}
              onKeyDown={
                card.path
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(card.path);
                      }
                    }
                  : undefined
              }
              value={
                countsLoading ? (
                  <span className="dashboard-stat-shimmer" />
                ) : (
                  formatCount(card.value)
                )
              }
              title={card.title}
              chart={
                <CIcon icon={card.icon} height={28} className="text-white opacity-75" />
              }
            />
          </CCol>
        ))}
      </CRow>

      <CCard className="dashboard-chart-card">
        <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <strong>{t(dashboard.growth_chart_title)}</strong>
          <CButtonGroup role="group" aria-label="Period filter">
            {periodOptions.map((option) => (
              <CButton
                key={option}
                color={period === option ? "primary" : "secondary"}
                size="sm"
                onClick={() => setPeriod(option)}
              >
                {t(dashboard.period[option])}
              </CButton>
            ))}
          </CButtonGroup>
        </CCardHeader>
        <CCardBody>
          <div className="dashboard-chart-container">
            {chartLoading ? (
              <div className="dashboard-chart-shimmer shimmer-bg" />
            ) : graph.length === 0 ? (
              <div className="dashboard-chart-empty text-center text-white-50">
                {t("admin.table_no_data_available")}
              </div>
            ) : (
              <CChartLine data={chartData} options={chartOptions} />
            )}
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

Dashboard.breadCrumbArray = [
  {
    name: breadcrumb_key.dashboard,
    pathname: routes.dashboard,
    active: true,
  },
];

export default Dashboard;
